// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { SupplierDocumentListItem } from 'src/app/shared/models/supplier/supplier-document-list-item.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class SuppliersService {

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService
  ) { }

  /**
   * Create a new supplier
   *
   * @since 1.0.0
   *
   * @param supplier Supplier data
   * @returns Observable<any>
   */
  create(supplier: any): Observable<any> {
    return this.apiService.apiCall('POST', 'suppliers', supplier);
  }

  /**
   * Add a employee to supplier employees
   *
   * @since 1.0.0
   * @returns Observable<any>
   */
  createEmployee(supplierId: string, employee: any): Observable<any> {
    return this.apiService.apiCall('POST', `suppliers/${supplierId}/employees`, employee);
  }

  /**
   * Add a document to supplier documents
   *
   * @since 1.0.0
   * @returns Observable<any>
   */
  createDocument(supplierId: string, employee: any): Observable<any> {
    return this.apiService.apiCall('POST', `suppliers/${supplierId}/documents`, employee);
  }

  /**
   * Get suppliers list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any, register?: boolean): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    if (register) {
      return this.apiService.apiCall('GET', 'suppliers/register', params);
    }
    return this.apiService.apiCall('GET', 'suppliers', params);
  }

  /**
   * Get an exportation file of all companies
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'suppliers/export', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of master's register companies only
   *
   * @since 1.0.0
   */
  getRegisterExport(): Observable<any> {
    return this.apiService.apiCall('GET', 'suppliers/export-register', {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get employees list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getEmployeesList(supplierId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `suppliers/${supplierId}/employees`, params);
  }

  /**
   * Get supplier's docs
   *
   * @since 1.0.0
   *
   * @param
   */
  getDocs(supplierId: string): Observable<any> {
    return this.apiService
      .apiCall("GET", `document-archives/supplier-docs/${supplierId}`)
      .pipe(
        catchError(err => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Get given supplierId data from back-end
   *
   * @since 1.0.0
   *
   * @param supplierId ID of the supplier to get
   */
  get(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `suppliers/${supplierId}`).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update supplier with given supplierId with given supplier data
   *
   * @since 1.0.0
   *
   * @param supplierId ID of supplier to update
   * @param data New supplier's data
   */
  update(supplierId: string, data: object): Observable<any> {
    return this.apiService.apiCall('PUT', `suppliers/${supplierId}`, data);
  }

  /**
   * Update supplier fields with given supplierId with given supplier data
   *
   * @since 1.0.0
   *
   * @param supplierId ID of supplier to update
   * @param data New supplier's data
   */
  updateFields(supplierId: string, data: object): Observable<any> {
    return this.apiService.apiCall("PATCH", `suppliers/${supplierId}`, data);
  }

  /**
   * Get given supplierId and employeeId data from back-end
   *
   * @since 1.0.0
   */
  updateEmployee(supplierId: string, employeeId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PUT', `suppliers/${supplierId}/employees/${employeeId}`, data).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get given supplierId and documentId data from back-end
   *
   * @since 1.0.0
   */
  updateDocument(supplierId: string, documentId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PUT', `suppliers/${supplierId}/documents/${documentId}`, data).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Delete permanently a supplier
   *
   * @since 1.0.0
   *
   * @param supplierId ID of the supplier
   */
  delete(supplierId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `suppliers/${supplierId}`);
  }

  /**
   * Delete permanently a supplier's employee
   *
   * @since 1.0.0
   */
  deleteEmployee(supplierId: string, employeeId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `suppliers/${supplierId}/employees/${employeeId}`);
  }

  /**
   * Delete permanently a supplier's employee
   *
   * @since 1.0.0
   */
  deleteDocument(supplierId: string, documentId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `suppliers/${supplierId}/documents/${documentId}`);
  }

  /**
   * Get suppliers list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSupplierDocumentsList(supplierId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `suppliers/${supplierId}/documents`, params);
  }
  /**
   * Get an exportation file of all Employees of supplier
   *
   * @since 1.0.0
   */
  getSupplierEmployeesExport(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `suppliers/${supplierId}/export-employees`, {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all documents of supplier
   *
   * @since 1.0.0
   */
  getSupplierDocumentsExport(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `suppliers/${supplierId}/export-documents`, {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get enabled Master list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getEnabledMasterList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `suppliers/enabled-master`, params);
  }

  /**
   * Get Master list with only Access Register for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getEnabledAccessRegisterList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `suppliers/enabled-master-access-register`, params);
  }

  /** 
   *  N E W 
   * @version 2.0
   */

  /**
  * Create a new supplier
  *
  * @since 1.0.0
  *
  * @param supplier Supplier data
  * @returns Observable<any>
  */
  createV2(supplier: any): Observable<any> {
    supplier.companyName = supplier.name;
    supplier.webSite = supplier.webSiteUrl;
    return this.apiService.apiCall('POST', 'users/no-registration', supplier, {}, true, true);
  }

  /**
  * Create a new supplier
  *
  * @since 1.0.0
  *
  * @param supplier Supplier data
  * @returns Observable<any>
  */
  createInvitationNfcAccess(supplier: any): Observable<any> {
    return this.apiService.apiCall('POST', 'users/invitation-nfc-access', supplier, {}, true, true);
  }

  /**
   * Create a new supplier by Master
   *
   * @since 1.0.0
   *
   * @param supplier Supplier data
   * @returns Observable<any>
   */
  createByMaster(data: any): Observable<any> {
    return this.apiService.apiCall('POST', 'users/by-master', data, {}, true, true);
  }

  /**
   * Get suppliers list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   * @param [master] Only users associated to master company
   * @param [allMasters] All users associated with at least one master company
   */
  getListV2(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any, master?: boolean, allMasters?: boolean): Observable<ListApiResponseV2> {
    if (!sort) {
      sort = "companyName";
      sortVersus = "asc"
    }
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    if (master) {
      return this.apiService.apiCall('GET', 'users/master-associated', params, {}, true, true);
    } else if (allMasters) {
      return this.apiService.apiCall('GET', 'users/all-master-associated', params, {}, true, true);
    }
    return this.apiService.apiCall('GET', 'users/performers', params, {}, true, true);
  }

  /**
   * Get suppliers list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   * @param [master] Only users associated to master company
   * @param [allMasters] All users associated with at least one master company
   */
  getListVOnlyNfc(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', 'users/in-access-register', params, {}, true, true);
  }

  /**
   * Get given supplierId data from back-end
   *
   * @since 1.0.0
   *
   * @param supplierId ID of the supplier to get
   */
  getV2(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `users/${supplierId}`, {}, {}, true, true).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Same endpoint as `getV2`, but WITHOUT reporting the error to the user.
   *
   * It exists for the callers that resolve MANY suppliers at once to turn ids into names:
   * `getV2` shows a toast per failure, so a page resolving thirty ids would bury the user
   * under thirty toasts, and a name that cannot be resolved is not something to interrupt
   * them for. The error still reaches the caller, which decides how to degrade.
   *
   * Use `getV2` for a single supplier the user explicitly asked for.
   *
   * @since 3.0.0
   *
   * @param supplierId ID of the supplier to get
   */
  getV2Silent(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `users/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Get given supplierId extended data from back-end
   *
   * @since 1.0.0
   *
   * @param supplierId ID of the supplier to get
   */
  getSupplierInfoExtended(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `creditsafe-data/user/${supplierId}`, {}, {}, true, true).pipe(
      catchError(err => {
        // this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get suppliers list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSupplierDocumentsListV2(supplierId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2<SupplierDocumentListItem>> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `suppliers/${supplierId}/documentsv2`, params);
  }

  /**
   * Get employees list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getEmployeesListV2(supplierId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `users/${supplierId}/employees`, params, {}, true, true);
  }

  /**
   * Get an exportation file of all documents of supplier
   *
   * @since 1.0.0
   */
  getSupplierDocumentsExportV2(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `suppliers/${supplierId}/export-documentsv2`, {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all documents of supplier
   *
   * @since 1.0.0
   */
  getSupplierOwnDocumentsExportV2(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `users/${supplierId}/export-own-documents`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all Employees of supplier
   *
   * @since 1.0.0
   */
  getSupplierEmployeesExportV2(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `users/${supplierId}/export-employees`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get supplier access logs list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getAccessLogs(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', 'access-logs', params, {}, true, true);
  }

  /**
   * Get an exportation file of all companies
   *
   * @since 1.0.0
   */
  getExportAccessLogs(): Observable<any> {
    return this.apiService.apiCall('GET', 'access-logs/export', {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get supplier email sent list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getEmailSent(supplierId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `email-to-users/recipient/${supplierId}`, params, {}, true, true);
  }

  /**
   * Get an exportation file of all email sent to supplier
   *
   * @since 1.0.0
   */
  getSupplierEmailSentExport(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `email-to-users/recipient/${supplierId}/export`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get supplier email sent list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getEmailSentToMaster(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `email-to-users/all-master`, params, {}, true, true);
  }

  /**
   * Get an exportation file of all email sent to master
   *
   * @since 1.0.0
   */
  getExportEmailSentToMaster(): Observable<any> {
    return this.apiService.apiCall('GET', `email-to-users/all-master-export`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get subscripiton supplier email sent list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getEmailSubscriptions(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `email-to-users/all-subscriptions`, params, {}, true, true);
  }

  /**
   * Get an exportation file of all email subscription sent to supplier
   *
   * @since 1.0.0
   */
  getExportEmailSubscriptions(): Observable<any> {
    return this.apiService.apiCall('GET', `email-to-users/all-subscriptions-export`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );

  }

  /**
   * Get documents supplier email sent list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getEmailDocuemnts(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `email-to-users/all-documents`, params, {}, true, true);
  }

  /**
   * Get an exportation file of all email documents sent to supplier
   *
   * @since 1.0.0
   */
  getExportEmailDocuments(): Observable<any> {
    return this.apiService.apiCall('GET', `email-to-users/all-documents-export`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );

  }

  /**
   * Get master associated to supplier list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getMasterAssociatedSupplier(supplierId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `users/${supplierId}/master-to-supplier`, params, {}, true, true);
  }

  /**
    * Get an exportation file of all Employees of supplier
    *
    * @since 1.0.0
    */
  getMasterAssociatedSupplierExport(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `users/${supplierId}/export-master-to-supplier`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get master associated to supplier list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getMasterInvitationSupplier(supplierId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `users/${supplierId}/master-invitation-supplier`, params, {}, true, true);
  }

  /**
    * Get an exportation file of all Employees of supplier
    *
    * @since 1.0.0
    */
  getMasterInvitationSupplierExport(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `users/${supplierId}/export-master-invitation-supplier`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  /**
   * Import suppliers from excel file
   *
   * @since 1.0.0
   *
   * @param suppliers Supplier data
   * @returns Observable<any>
   */
  importSuppliers(suppliers: any): Observable<any> {
    return this.apiService.apiCall('POST', 'imports', suppliers, {}, true, true);
  }
  
  /**
   * Import access register suppliers from excel file
   *
   * @since 1.0.0
   *
   * @param suppliers Supplier data
   * @returns Observable<any>
   */
  importAccessRegister(suppliers: any): Observable<any> {
    return this.apiService.apiCall('POST', 'import-access-registers', suppliers, {}, true, true);
  }

  /**
 * Import one supplier
 *
 * @since 1.0.0
 *
 * @param supplier Supplier data
 * @returns Observable<any>
 */
  importSingleSuppliers(supplier: any): Observable<any> {
    return this.apiService.apiCall('POST', 'imports/single', supplier, {}, true, true);
  }
  
  /**
   * Import suppliers into cleaning from excel file
   *
   * @since 1.0.0
   *
   * @param suppliers Supplier data
   * @returns Observable<any>
   */
  cleanSuppliers(suppliers: any): Observable<any> {
    return this.apiService.apiCall('POST', 'process-blacklists', suppliers, {}, true, true);
  }
  
  /**
   * Clean suppliers from excel file
   *
   * @since 1.0.0
   *
   * @param suppliers Supplier data
   * @returns Observable<any>
   */
  importBlacklist(suppliers: any): Observable<any> {
    return this.apiService.apiCall('POST', 'blacklists', suppliers, {}, true, true);
  }
  
  /**
   * Send durc email to supplier
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID
   * @returns Observable<any>
   */
  sendDurcEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-durc-email/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Send the DURC reminder ("sollecito") e-mail to the supplier on demand, i.e.
   * the very same e-mail the automatic cron would send, advancing the reminder
   * cycle server-side (ownDocuments[durc].notice / .sentEmail / .status).
   *
   * NOT idempotent: every successful call sends an e-mail and increments the
   * reminder counter, so the caller MUST keep the trigger disabled while the
   * request is in flight.
   * Allowed to "admin" and "operatore" roles only (403 otherwise). All the
   * preconditions checked by the front-end (DURC present, no exemption, DURC not
   * valid, PEC set) are re-validated server-side (404/409).
   * On success the response carries { docType, reminderNumber, variant, sentTo,
   * mastersNotified }: reload the supplier afterwards.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID (Mongo ObjectId)
   * @returns Observable<any>
   */
  sendDurcReminderEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-durc-reminder-email/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Notify the supplier, over PEC, that the document filed as DURC in the Albo is
   * a different document, and ask it to upload the right one.
   *
   * This is NOT a reminder: it does not consume a step of the reminder cycle, does
   * not postpone the automatic e-mail and does not touch the supplier at all
   * (notice / sentEmail / status of the DURC own-document are left untouched), so
   * the caller only needs to invalidate the sent-e-mails list, not to re-read the
   * supplier.
   * Idempotent: calling it again sends the very same e-mail, and there is no
   * server-side rate limit — the caller MUST ask for confirmation and keep the
   * trigger disabled while the request is in flight.
   * Allowed to "admin" and "operator" roles only (403 otherwise); PEC, paying
   * masters, DURC document, exemption and liquidation state are re-validated
   * server-side (404/409).
   * Unlike send-durc-email / send-pac-email the 201 is returned only when the PEC
   * really left, so the success can be trusted.
   * On success the response carries { docType, variant: 'wrongDocument', sentTo }.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID (Mongo ObjectId)
   * @returns Observable<any>
   */
  sendDurcNotValidEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-durc-not-valid-email/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Send durc email to supplier
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID
   * @returns Observable<any>
   */
  sendVideoSecurityEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-video-security-email/${supplierId}`, {}, {}, true, true);
  }
  
  /**
   * Send PAC email to supplier
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID
   * @returns Observable<any>
   */
  sendPacEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-pac-email/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Send the PAC ("Patente a Crediti") reminder e-mail to the supplier on demand,
   * i.e. the very same e-mail the automatic cron would send, advancing the
   * reminder cycle server-side (ownDocuments[pac].notice / .sentEmail / .status).
   * Twin of sendDurcReminderEmail().
   *
   * NOT idempotent: every successful call sends an e-mail and increments the
   * reminder counter, so the caller MUST keep the trigger disabled while the
   * request is in flight.
   * Allowed to "admin" and "operatore" roles only (403 otherwise). All the
   * preconditions checked by the front-end (PAC document present, no PAC
   * exemption, supplier not declared as "non soggetto", PEC set) are re-validated
   * server-side (404/409).
   * On success the response carries { docType, reminderNumber, variant, sentTo }:
   * "variant" is "reminder" for the plain reminder (the PAC document becomes
   * notValid) or "update" when the PAC is valid and already has credits (points
   * update request, status untouched). No mastersNotified here: the PAC has no
   * escalation to the masters.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID (Mongo ObjectId)
   * @returns Observable<any>
   */
  sendPacReminderEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-pac-reminder-email/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Ask the supplier, over PEC, to update the credit points of a "Patente a
   * Crediti" already on file ("Richiesta aggiornamento punti Patente a Crediti").
   * Manual counterpart of the e-mail the automatic cycle already sends on its own.
   *
   * NOT idempotent: every successful call sends a real PEC and advances the
   * reminder counter server-side, so the caller MUST ask for confirmation, keep the
   * trigger disabled while the request is in flight and never retry automatically.
   * Allowed to "admin" and "operatore" roles only (403 otherwise). All the
   * preconditions checked by the front-end (PEC set, PAC document present WITH its
   * credits registered, no PAC exemption, supplier not declared as "non soggetto")
   * are re-validated server-side (404/409), together with the two this page cannot
   * see (at least one paying master linked, supplier not in liquidation).
   * On success the response carries { docType: 'pac', variant: 'update',
   * reminderNumber, sentTo }. The supplier changes server-side (last-send date and
   * reminder counter): the caller MUST re-read it.
   * Two error shapes need special casing by the caller, since HelperService cannot
   * render them: 401 with data.logout === true (force the logout) and 500 with
   * data.emailSent === true (the PEC really left, only its bookkeeping failed — do
   * not invite a retry, it would send a second e-mail to the supplier).
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID (Mongo ObjectId)
   * @returns Observable<any>
   */
  sendPacUpdateCreditsEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-pac-update-credits-email/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Reminder of the credit-points update request ("Richiesta aggiornamento punti
   * Patente a Crediti - Sollecito"). Twin of sendPacUpdateCreditsEmail(): same
   * request, same response shape, same preconditions and same errors, only the sent
   * text and the resulting state differ — this one also marks the credit licence as
   * expired, on top of advancing the reminder counter.
   * On success the response carries { docType: 'pac', variant: 'reminderUpdate',
   * reminderNumber, sentTo }.
   *
   * NOT idempotent, same caller duties as the twin: confirmation dialog, trigger
   * disabled while in flight, supplier re-read afterwards, no automatic retry.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID (Mongo ObjectId)
   * @returns Observable<any>
   */
  sendPacUpdateCreditsReminderEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall(
      'POST',
      `users/send-pac-update-credits-reminder-email/${supplierId}`,
      {},
      {},
      true,
      true
    );
  }

  /**
   * Notify the supplier, over PEC, that the document filed as "Patente a Crediti"
   * is only the receipt, which carries no credit number and is therefore worthless,
   * and ask it to fill in the data and upload the right document.
   * Twin of sendDurcNotValidEmail(), same semantics on a different document.
   *
   * This is NOT a reminder: it does not consume a step of the reminder cycle, does
   * not postpone the automatic e-mail and does not touch the supplier at all
   * (notice / sentEmail / status / credits of the PAC own-document are left
   * untouched), so the caller only needs to invalidate the sent-e-mails list, not
   * to re-read the supplier.
   * Idempotent: calling it again sends the very same e-mail, and there is no
   * server-side rate limit — the caller MUST ask for confirmation and keep the
   * trigger disabled while the request is in flight.
   * Allowed to "admin" and "operator" roles only (403 otherwise); PEC, paying
   * masters, PAC document, exemption, notCreditLicence and liquidation state are
   * re-validated server-side (404/409). There is no "document already valid" block:
   * the filed file may be the wrong one anyway.
   * Unlike send-pac-email the 201 is returned only when the PEC really left, so the
   * success can be trusted.
   * On success the response carries { docType, variant: 'wrongDocument', sentTo }.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID (Mongo ObjectId)
   * @returns Observable<any>
   */
  sendPacNotValidEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-pac-not-valid-email/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Notify the supplier, over PEC, that the document filed as "Patente a Crediti"
   * is not a credit licence at all but a different document, and ask it to fill in
   * the data and upload the right one. The text also carries a disclaimer about any
   * personal data contained in the uploaded file (artt. 4 and 9 GDPR).
   *
   * Third of the three sibling "not valid" notifications (@see
   * sendDurcNotValidEmail, @see sendPacNotValidEmail): same request/response and
   * same semantics, only the sent text differs. The back-end shares the very same
   * implementation as send-pac-not-valid-email, so preconditions, error codes and
   * their order are guaranteed aligned; only data.variant differs
   * ('otherDocument' here, 'wrongDocument' there).
   *
   * This is NOT a reminder and does not touch the supplier at all: the caller only
   * needs to invalidate the sent-e-mails list, not to re-read the supplier.
   * Idempotent and without server-side rate limit — the caller MUST ask for
   * confirmation and keep the trigger disabled while the request is in flight.
   * Allowed to "admin" and "operator" roles only (403 otherwise).
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID (Mongo ObjectId)
   * @returns Observable<any>
   */
  sendPacNotValidOtherEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-pac-not-valid-other-email/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Send privacy email to supplier
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID
   * @returns Observable<any>
   */
  sendPrivacyEmail(supplierId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-privacy-email/${supplierId}`, {}, {}, true, true);
  }

  /**
   * Send privacy email to all active suppliers of a company
   *
   * @since 1.0.0
   *
   * @param companyId Company ID
   * @returns Observable<any>
   */
  sendPrivacyEmailAll(companyId: string): Observable<any> {
    return this.apiService.apiCall('POST', `users/send-privacy-email-all/${companyId}`, {}, {}, true, true);
  }

  /**
   * Mark the supplier's PAC (credit licence) document as "not received".
   * The back-end sets the PAC document status to "notReceived", disables
   * automatic e-mails for this supplier and notifies the linked companies.
   * The operation is idempotent.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID (Mongo ObjectId)
   * @returns Observable<any>
   */
  setPacNotReceived(supplierId: string): Observable<any> {
    return this.apiService.apiCall('PATCH', `users/${supplierId}/set-pac-not-received`, {}, {}, true, true);
  }

  /**
   * Mark the supplier's DURC document as "not received".
   * Twin of setPacNotReceived(): the back-end sets every "durc" own-document to
   * status "notReceived", sets disableSendEmailDurc (suspending only the DURC
   * automatic reminders, not the PAC/SOA ones) and notifies the linked companies.
   * The operation is idempotent and has no undo endpoint.
   * A 404 is returned when the supplier has no "durc" own-document at all: its
   * localised "message" is meant to be shown to the operator as is.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ID (Mongo ObjectId)
   * @returns Observable<any>
   */
  setDurcNotReceived(supplierId: string): Observable<any> {
    return this.apiService.apiCall('PATCH', `users/${supplierId}/set-durc-not-received`, {}, {}, true, true);
  }

  /**
   * Get imports list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getImportList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', 'imports', params, {}, true, true);
  }

  /**
   * Get given importId data from back-end
   *
   * @since 1.0.0
   *
   * @param importId ID of the report to get
   */
  getImport(importId: string): Observable<any> {
    return this.apiService.apiCall('GET', `imports/${importId}`, {}, {}, true, true).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get an exportation file of all import
   *
   * @since 1.0.0
   */
  getExportImportReport(): Observable<any> {
    return this.apiService.apiCall('GET', 'imports/export', {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get reports list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getReportList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', 'reports', params, {}, true, true);
  }

  /**
   * Get given reportId data from back-end
   *
   * @since 1.0.0
   *
   * @param reportId ID of the report to get
   */
  getReport(reportId: string): Observable<any> {
    return this.apiService.apiCall('GET', `reports/${reportId}`, {}, {}, true, true).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get an exportation file of all report
   *
   * @since 1.0.0
   */
  getExportReport(): Observable<any> {
    return this.apiService.apiCall('GET', 'reports/export', {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all companies
   *
   * @since 1.0.0
   */
  getExportV2(type: string, companyId?: string, data?: any): Observable<any> {
    // if (type === "paying") { // assessment, listv2, performer, search, tank

    // }
    if (type === "tank") {
      return this.apiService.apiCall('GET', 'users/export-tank', {}, { responseType: 'blob' }, true, true)
        .pipe(
          catchError(err => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
    if (type === "listv2") {
      return this.apiService.apiCall('GET', 'users/master-associated-export', {}, { responseType: 'blob' }, true, true)
        .pipe(
          catchError(err => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
    if (type === "listv3") {
      return this.apiService.apiCall('GET', `users/master-associated-export/${companyId}`, {}, { responseType: 'blob' }, true, true)
        .pipe(
          catchError(err => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
    if (type === "all") {
      return this.apiService.apiCall('GET', 'users/all-master-associated-export', {}, { responseType: 'blob' }, true, true)
        .pipe(
          catchError(err => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
    if (type === "search") {
      const params: any = {  };
      if (data) {
        Object.keys(data).forEach(key => {
          const newParamKey = `search[${key}]`;
          const newParamValue = data[key];
          params[newParamKey] = newParamValue;
        });
      }
      return this.apiService.apiCall('GET', 'users/search-all-master-associated-export', params, { responseType: 'blob' }, true, true)
        .pipe(
          catchError(err => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
    if (type === "searchpec") {
      const params: any = {};
      const newParamKey = `search[pec]`;
      const newParamValue = 'void';
      params[newParamKey] = newParamValue;

      return this.apiService.apiCall('GET', 'users/all-master-associated-nopec-export', params, { responseType: 'blob' }, true, true)
        .pipe(
          catchError(err => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
    if (type === "searchDisabledEmail") {
      // const params: any = {};
      // const newParamKey = `search[disableSendEmail]`;
      // const newParamValue = true;
      // params[newParamKey] = newParamValue;

      return this.apiService.apiCall('GET', 'users/all-master-associated-disabled-email-export', {}, { responseType: 'blob' }, true, true)
        .pipe(
          catchError(err => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
    if (type === "performers") {
      return this.apiService.apiCall('GET', 'users/performers-export', {}, { responseType: 'blob' }, true, true)
        .pipe(
          catchError(err => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
    if (type === "onlyNfc") {
      return this.apiService.apiCall('GET', 'users/in-access-register-export', {}, { responseType: 'blob' }, true, true)
        .pipe(
          catchError(err => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
    return this.apiService.apiCall('GET', 'users/export', {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get new suppliers list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getNewSuppliersInTank(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', 'users/new-in-tank', params, {}, true, true);
  }

  /**
   * Get employees list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getCompanyInvitationList(companyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `users/${companyId}/invitation-list`, params, {}, true, true);
  }

  /**
   * Get employees list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getAdminInvitationList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `users/invitation-list-admin`, params, {}, true, true);
  }

  /**
   * Get an exportation file of all companies
   *
   * @since 1.0.0
   */
  getCompanyInvitationExport(companyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `users/${companyId}/invitation-list-export`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all companies
   *
   * @since 1.0.0
   */
  getAdminInvitationExport(): Observable<any> {
    return this.apiService.apiCall('GET', `users/invitation-list-admin-export`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  /**
   * Get list of suppliers in blacklist for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
    getCleaning(adminId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
      const params: any = { adminId, start, limit, sort, sortVersus };
      if (search) {
        Object.keys(search).forEach(key => {
          const newParamKey = `search[${key}]`;
          const newParamValue = search[key];
          params[newParamKey] = newParamValue;
        });
      }
  
      return this.apiService.apiCall('GET', `process-blacklists/${adminId}`, params, {}, true, true);
    }
  
  
  /**
   * Get list of suppliers in blacklist for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getBlacklist(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `blacklists`, params, {}, true, true);
  }

  /**
   * Get an exportation file
   *
   * @since 1.0.0
   */
  getSupplierCleaningExport(adminId: string): Observable<any> {
    return this.apiService.apiCall('GET', `process-blacklists/${adminId}/export`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file
   *
   * @since 1.0.0
   */
  getSupplierBlacklistExport(): Observable<any> {
    return this.apiService.apiCall('GET', `blacklists/export`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Set supplier into Blacklist
   *
   * @since 1.0.0
   */
  setWhitelist(blacklistId: string): Observable<any> {
    return this.apiService.apiCall("DELETE", `blacklists/${blacklistId}`, {}, {}, true, true);
  }

  /**
   * Update imported suppliers status to Professional
   *
   * @since 1.0.0
   */
  updateImportProfessionals(importId: string, data: any): Observable<any> {
    return this.apiService.apiCall("PATCH", `imports/professionals/${importId}`, data, {}, true, true);
  }

  /**
   * Set supplier into Whitelist/Blacklist
   *
   * @since 1.0.0
   */
  toggleBlacklist(itemId: string): Observable<any> {
    return this.apiService.apiCall("PATCH", `process-blacklists/${itemId}/toggle-blacklist`, {}, {}, true, true);
  }

  /**
   * Update supplier in process blacklist
   *
   * @since 1.0.0
   */
  updateProcessBlacklistItem(itemId: string, data: any): Observable<any> {
    return this.apiService.apiCall("PATCH", `process-blacklists/${itemId}`, data, {}, true, true);
  }

  /**
   * Empty process blacklist
   *
   * @since 1.0.0
   */
  deleteProcessBlacklist(adminId: string): Observable<any> {
    return this.apiService.apiCall("DELETE", `process-blacklists/${adminId}`, {}, {}, true, true);
  }
  /**
   * Update supplier fields with given supplierId with given supplier data
   *
   * @since 1.0.0
   *
   * @param supplierId ID of supplier to update
   * @param data New supplier's data
   */
  updateFieldsV2(supplierId: string, data: object): Observable<any> {
    return this.apiService.apiCall("PATCH", `users/${supplierId}`, data, {}, true, true);
  }

  /**
   * Start process new supplier
   *
   * @since 1.0.0
   *
   * @param supplierId ID of supplier to update
   */
  startProcessNewSupplier(supplierId: string): Observable<any> {
    const data = {
    }
    return this.apiService.apiCall("PATCH", `users/${supplierId}/start-process-new`, data, {}, true, true);
  }

  /**
   * Start process new supplier
   *
   * @since 1.0.0
   *
   * @param supplierId ID of supplier to update
   */
  endProcessNewSupplier(supplierId: string): Observable<any> {
    const data = {
    }
    return this.apiService.apiCall("PATCH", `users/${supplierId}/end-process-new`, data, {}, true, true);
  }

  /**
   * Reject new supplier
   *
   * @since 1.0.0
   *
   * @param supplierId ID of supplier to update
   */
  endRejectNewSupplier(supplierId: string): Observable<any> {
    const data = {
    }
    return this.apiService.apiCall("PATCH", `users/${supplierId}/reject-process-new`, data, {}, true, true);
  }
  /**
   * Set/Unset BiPy service to Supplier
   *
   * @since 1.0.0
   *
   * @param supplierId ID of supplier to update
   * @param companyId ID of company
   */
  toggleBipy(supplierId: string, companyId: string, companyBipyId: string): Observable<any> {
    const data = {
      companyId,
      companyBipyId,
    }
    return this.apiService.apiCall("PATCH", `users/${supplierId}/toggle-bipy`, data, {}, true, true);
  }

  /**
   * Get docs param
   * 
   * @since 1.0.0
   */
  getDocsParams(): Observable<any> {
    const params: any = { start: 0, limit: 10, sort: 'code', sortVersus: 'asc' };
    return this.apiService.apiCall('GET', 'params', params, {}, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Update supplier doc parm
   * 
   * @since 1.0.0
   */
  updateParDoc(paramId: string, numberValue: string): Observable<any> {
    const data = {
      numberValue
    }
    return this.apiService.apiCall("PATCH", `params/${paramId}`, data, {}, true, true);
  }

  /**
   * Company Master: Add supplier association
   * 
   * @since 1.0.0
   */
  addSupplierAssociation(companyId: string, supplierId: string): Observable<any> {
    const data = {};
    return this.apiService.apiCall("PATCH", `users/${supplierId}/set-company/${companyId}`, data, {}, true, true);
  }
  
  /**
   * Set/Unset BiPy service to Supplier
   *
   * @since 1.0.0
   *
   * @param supplierId ID of supplier to update
   * @param companyId ID of company
   */
  setVideoSecurity(supplierId: string): Observable<any> {
    const data = {}
    return this.apiService.apiCall("PATCH", `users/${supplierId}/set-supplier-video-security`, data, {}, true, true);
  }

  /**
   * Company Master: Delete supplier association
   * 
   * @since 1.0.0
   */
  deleteSupplierAssociation(companyId: string, supplierId: string): Observable<any> {
    const data = {};
    return this.apiService.apiCall("PATCH", `users/${supplierId}/unset-company/${companyId}`, data, {}, true, true);
  }

  /**
   * Company Master: Delete supplier association
   * 
   * @since 1.0.0
   */
  deleteSupplierMultiAssociation(companiesId: string[], supplierId: string): Observable<any> {
    const data = {
      companies: companiesId
    };
    return this.apiService.apiCall("PATCH", `users/${supplierId}/unset-multi-company`, data, {}, true, true);
  }

  /**
   * Company Master: Delete supplier association
   * 
   * @since 1.0.0
   */
  permanentlyDeleteSupplier(supplierId: string): Observable<any> {
    const data = {};
    return this.apiService.apiCall("DELETE", `users/${supplierId}/permanently`, data, {}, true, true);
  }

  /**
   * Delete supplier email
   * 
   * @since 1.0.0
   */
  deleteSupplierEmail(supplierId: string, emailId: string): Observable<any> {
    const data = {};
    return this.apiService.apiCall("DELETE", `users/${supplierId}/delete-email/${emailId}`, data, {}, true, true);
  }

  /**
   * Update supplier own document with given supplier data
   *
   * @since 1.0.0
   *
   * @param supplierId ID of supplier to update
   * @param data New supplier's data
   */
  updateDocumentV2(supplierId: string, data: object): Observable<any> {
    return this.apiService.apiCall("PATCH", `users/${supplierId}/doc`, data, {}, true, true);
  }

  /**
   * Send invitation to suppliers
   *
   * @since 1.0.0
   *
   * @param data New supplier's data
   */
  inviteSuppliers(data: object): Observable<any> {
    return this.apiService.apiCall("POST", `users/send-invitation-suppliers`, data, {}, true, true);
  }

  /**
   * Send invitation to suppliers
   *
   * @since 1.0.0
   *
   * @param data New supplier's data
   */
  temporaryAdminAccess(supplierId: string, data: object): Observable<any> {
    return this.apiService.apiCall("POST", `users/${supplierId}/admin-access`, data, {}, true, true);
  }

  /**
   * Delete supplier own document with given supplier data
   * 
   * @since 1.0.0
   * 
   * @param supplierId 
   * @param docType 
   * @returns 
   */
  deleteSupplierOwnDocument(supplierId: string, docType: string): Observable<any> {
    return this.apiService.apiCall("DELETE", `users/${supplierId}/delete-own-document/${docType}`, {}, {}, true, true);
  }

  // BEGIN - Supplier platform notes (suppliers back-end)

  /**
   * Get the paginated list of platform notes for a supplier.
   *
   * Suppliers back-end. Response envelope: { status, data: SupplierNote[], totalCount }.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ObjectId
   * @param [start] Pagination offset
   * @param [limit] Page size (1-100)
   * @param [sort] Sort field
   * @param [sortVersus] Sort direction ('asc' | 'desc')
   * @param [search] Column filters (e.g. { title, createdAt, createdBy })
   */
  getSupplierNotes(supplierId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<any> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const value = search[key];
        if (value !== null && typeof value !== 'undefined' && value !== '') {
          params[`search[${key}]`] = value;
        }
      });
    }
    return this.apiService.apiCall('GET', `platform-notes/supplier/${supplierId}`, params, {}, true, true);
  }

  /**
   * Get the "created by" dropdown data for a supplier's notes.
   *
   * Suppliers back-end. Response envelope: { status, data: Array<{ _id, firstName, lastName }>, totalCount }.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ObjectId
   * @param [start] Pagination offset
   * @param [limit] Page size (omit to get all creators)
   * @param [sort] Sort field
   * @param [sortVersus] Sort direction ('asc' | 'desc')
   * @param [search] Filters (e.g. { general })
   */
  getSupplierNoteCreators(supplierId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<any> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const value = search[key];
        if (value !== null && typeof value !== 'undefined' && value !== '') {
          params[`search[${key}]`] = value;
        }
      });
    }
    return this.apiService.apiCall('GET', `platform-notes/supplier/${supplierId}/creators`, params, {}, true, true);
  }

  /**
   * Create a new platform note for a supplier.
   *
   * Suppliers back-end. The server derives createdBy/supplierId from the session token,
   * so they must NOT be sent in the payload.
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ObjectId
   * @param data { title?, description?, file?: Array<{ _id }> } (file max 1 element)
   */
  createSupplierNote(supplierId: string, data: any): Observable<any> {
    return this.apiService.apiCall('POST', `platform-notes/supplier/${supplierId}`, data, {}, true, true);
  }

  /**
   * Partially update a supplier platform note.
   *
   * Suppliers back-end. Only the provided fields are changed.
   * file: [{ _id }] replaces the attachment, [] removes it, absent keeps it unchanged.
   *
   * @since 1.0.0
   *
   * @param noteId Note ObjectId
   * @param data Partial note data
   */
  updateSupplierNote(noteId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PATCH', `platform-notes/${noteId}`, data, {}, true, true);
  }

  /**
   * Permanently delete a supplier platform note (hard delete).
   *
   * Suppliers back-end.
   *
   * @since 1.0.0
   *
   * @param noteId Note ObjectId
   */
  deleteSupplierNote(noteId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `platform-notes/${noteId}`, {}, {}, true, true);
  }

  /**
   * Export all notes of a supplier as an .xlsx binary file.
   *
   * Suppliers back-end. Returns a Blob (not the JSON envelope).
   *
   * @since 1.0.0
   *
   * @param supplierId Supplier ObjectId
   */
  getSupplierNotesExport(supplierId: string): Observable<any> {
    return this.apiService.apiCall('GET', `platform-notes/supplier/${supplierId}/export`, {}, { responseType: 'blob' }, true, true)
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get the paginated list of platform notes across ALL suppliers, with the
   * parent supplier populated on each row (companyName, vatNumber, status).
   *
   * NOTE: this endpoint does not exist yet on the suppliers back-end as of
   * writing — every other platform-notes endpoint is scoped to a single
   * supplierId (@see getSupplierNotes). This method assumes the back-end team
   * will add an unscoped `GET platform-notes` route returning the same
   * envelope as getSupplierNotes ({ status, data: SupplierNoteListItem[],
   * totalCount }), with each item's `supplier` field populated with at least
   * { _id, companyName, vatNumber, status, notRegistered }, and accepting the
   * flat field names below for both `sort` and `search[...]` (the back-end
   * maps them to the underlying populated/nested path): `companyName`,
   * `vatNumber`, `status` (supplier-side), plus `title` (the note's own
   * field). Verify/align this contract with the back-end before relying on it.
   *
   * @since 1.0.0
   *
   * @param [start] Pagination offset
   * @param [limit] Page size (1-100)
   * @param [sort] Sort field ('companyName' | 'vatNumber' | 'status' | 'title' | 'createdAt')
   * @param [sortVersus] Sort direction ('asc' | 'desc')
   * @param [search] Column filters (e.g. { companyName, vatNumber, status, title })
   */
  getPlatformNotesList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<any> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const value = search[key];
        if (value !== null && typeof value !== 'undefined' && value !== '') {
          params[`search[${key}]`] = value;
        }
      });
    }
    return this.apiService.apiCall('GET', 'platform-notes', params, {}, true, true);
  }

  // END - Supplier platform notes
}
